import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { DefaultLayout } from "../../../../../../layouts/Default";
import { TopNavigation } from "../../../../../../themes/Default/components/TopNavigation";
import { Button, ButtonBack } from "../../../../../../themes/Default/components/TopNavigation/components/Button";
import { Tabs } from "../../../../../../themes/Default/components/Tabs";
import { Main } from "../../../../../../themes/Default/components/Main";
import { Breadcrumps } from "../../../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../../../themes/Default/components/Breadcrumps/components/Link";
import { Form } from "../../../../../../themes/Default/components/Form";
import { Row } from "../../../../../../themes/Default/components/Form/components/Row";
import { Field } from "../../../../../../themes/Default/components/Form/components/Row/components/Field";
import { Title } from "../../../../../../themes/Default/components/Title";
import { Wizard } from "../../../../../../themes/Default/components/Wizard";
import { Step } from "../../../../../../themes/Default/components/Wizard/Step";
import { groupProgressManagement } from "../../../../../../hooks/api/GroupProgressManagement/GroupProgressManagement";
import { groupTypesManagement } from "../../../../../../hooks/api/GroupTypesManagement/GroupTypesManagement";
import { prepareSelect } from "../../../../../../services/core/Select";
import { Select } from "../../../../../../themes/Default/components/Form/components/Select";
import { List } from "../../../../../../themes/Default/components/List";
import { Header as ListHeader } from "../../../../../../themes/Default/components/List/components/Header";
import { Title as ListTitle } from "../../../../../../themes/Default/components/List/components/Header/components/Title";
import { Body as ListBody } from "../../../../../../themes/Default/components/List/components/Body";
import { TableHead } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead";
import { TableRow } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow";
import { TableHeadCell } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow/components/TableHeadCell";
import { TableBody } from "../../../../../../themes/Default/components/List/components/Body/components/TableBody";
import { TableDataCell } from "../../../../../../themes/Default/components/List/components/Body/components/TableBody/components/TableDataCell";
import { Slide, toast, ToastContainer } from "react-toastify";
import { Toaster } from "../../../../../../themes/Default/components/Toaster";
import { Input } from "../../../../../../themes/Default/components/Form/components/Input";

const AddProgressType = () => {
    const { t } = useTranslation("common");
    const formRef = useRef(null);
    const navigate = useNavigate();
    const [formErrors, setFormErrors] = useState([]);
    const { groupProgressId } = useParams();
    const [groupTypeId, setGroupTypeId] = useState(0);
    const [groupProgressName, setGroupProgressName] = useState("");
    const [groupTypes, setGroupTypes] = useState([]);
    const [attachedGroupProgressTypes, setAttachedGroupProgressTypes] = useState([]);

    const tabs = [
        { title: t("plans.title"), onClick: () => gotoPlansTab() },
        { title: t("groupTypes.title"), onClick: () => gotoTypeTab() },
        { title: t("groups.progress"), isActive: true },
        { title: t("groups.progressTest"), onClick: () => gotoProgressTestTab() },
    ];

    const gotoTypeTab = () => {
        navigate("/settings/groups/types");
    };

    const gotoPlansTab = () => {
        navigate("/settings/groups/plans");
    };
    
    const gotoProgressTestTab = () => {
        navigate("/settings/groups/progress-tests");
    };
    
    const { 
        useGroupTypesListMutation,
    } = groupTypesManagement;

    const { 
        useListGroupProgressTypeMutation,
        useAddGroupProgressTypeMutation,
        useDeleteGroupProgressTypeMutation,
        useViewGroupProgressMutation
    } = groupProgressManagement;

    const [groupTypesList] = useGroupTypesListMutation();
    const [listGroupProgressType] = useListGroupProgressTypeMutation();
    const [addGroupProgressType] = useAddGroupProgressTypeMutation();
    const [deleteGroupProgressType] = useDeleteGroupProgressTypeMutation();
    const [viewGroupProgress] = useViewGroupProgressMutation();

    const gotoProgressStep = () => {
        navigate('/settings/groups/progress/'+groupProgressId+'/edit');
    };

    const onRemove = (groupTypeId) => {
        toast.warn(
            ({ closeToast }) => (
                <Toaster
                    closeToast={closeToast}
                    description={t("areYouSure")}
                    optionNo={t("no")}
                    optionYes={t("yes")}
                    onClick={() => {
                        closeToast();
                        deleteGroupProgressType({settingsGroupProgressId: groupProgressId, groupTypeId}).then((result) => {
                            if (result?.error) {
                                toast.error(result.error.data.error.responseMessage);
                            }else{
                                toast.info(result?.data?.message);
                                loadAttachedGroupProgressTypes();
                            }
                        })
                    }}
                />
            ),
            {
                autoClose: false,
                progress: false,
                closeButton: false,
            },
        );
    };

    const gotoEdit = (groupProgressTypeId) => {
        navigate("/settings/groups/progress/" + groupProgressId + "/type/" + groupProgressTypeId + "/edit");
    };

    const onSave = () => {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    };

    const onSaved = (values) => {
        addGroupProgressType({body: values, settingsGroupProgressId: groupProgressId, groupTypeId}).then((result) => {
            if (result?.error) {
                setFormErrors(result?.error?.data?.error?.violations);
                toast.error(result.error.data.error.responseMessage);
            } else {
                loadAttachedGroupProgressTypes();
            }
            console.log(result);
        });
    };

    const onAddOptions = (groupProgressTypeId) => {
        navigate("/settings/groups/progress/"+groupProgressId+"/type/"+groupProgressTypeId+"/options/add");
    };

    const loadAttachedGroupProgressTypes = () => {
        listGroupProgressType(groupProgressId).then((result) => {
            setAttachedGroupProgressTypes(result?.data?.records);
        });
    }

    const loadGroupProgressName = () => {
        viewGroupProgress(groupProgressId).then((result) => {
            setGroupProgressName(result?.data?.name);
        });
    }

    useEffect(() => {
        groupTypesList().then((result) => {
            let options = [];
            result?.data?.records.map((groupType) => {
                options.push(
                    {
                        id: groupType.id,
                        name: groupType.name
                    }
                );
            })
            setGroupTypes(prepareSelect(options));
        });
        loadAttachedGroupProgressTypes();
        loadGroupProgressName();
     }, []);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to={"/settings/groups/progress/" + groupProgressId + "/edit"}/>
                <Button title={t('save')} icon="fa-regular fa-floppy-disk" onClick={onSave}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title>{groupProgressName}</Title>
                <Breadcrumps>
                    <Link title={t("settings.title")} icon="fa-gear" />
                    <Link title={t("groups.title")} />
                    <Link title={t("Napredak")} />
                    <Link title={t('details')} isActive={true}/>
                </Breadcrumps>
                <br/>
                <br/>
                <br/>
                <Wizard>
                    <Step title={t('Naziv kolone')} isChecked={true} onClick={gotoProgressStep}/>
                    <Step title={t('Odabir tipova grupa')} isActive={true}  />
                    <Step title={t('Dodavanje vrednosti')} isChecked={true}  />
                </Wizard>
                <br/>
                <br/>
                <br/>
                <Formik
                    innerRef={formRef}
                    initialValues={{ 
                        groupTypes: "", 
                        reportColumnTitle: ""
                    }}
                    onSubmit={(values, { resetForm }) => {
                        onSaved(values);
                        resetForm();
                    }}
                >
                    {({ values, handleChange, setFieldValue, resetForm }) => (
                        <Form>
                            <Row>
                                <Field>
                                    <Select 
                                        name="groupTypes" 
                                        label={t("Tip grupe")}
                                        options={groupTypes}  
                                        selectedValue={values.groupTypes}
                                        onChange={(event) => {
                                            handleChange(event);
                                            setGroupTypeId(event.target.value);
                                        }}
                                        error={formErrors?.firstName}
                                    />
                                </Field>
                                <Field></Field>
                            </Row>
                            <Row>
                                <Field>
                                    <Input
                                        name="reportColumnTitle"
                                        label={t("Naslov u izvestaju za ovu kolonu")}
                                        required={true}
                                        value={values.reportColumnTitle}
                                        onChange={handleChange}
                                        error={formErrors?.reportColumnTitle}
                                    />
                                </Field>
                            </Row> 
                        </Form>
                    )}
                </Formik>
                <br />
                <List>
                    <ListHeader>
                        <ListTitle 
                            title={t('Tipovi grupa')}
                            subtitle="List of the group types who are attached to this group progress" 
                        />
                    </ListHeader>
                    <ListBody>
                        <TableHead>
                            <TableRow>
                                <TableHeadCell></TableHeadCell>
                                <TableHeadCell><span>{t('Tip grupe')}</span></TableHeadCell>
                                <TableHeadCell><span>{t('Boja')}</span></TableHeadCell>
                                <TableHeadCell><span>{t('Naslov u izvestaju za ovu kolonu')}</span></TableHeadCell>
                                <TableHeadCell></TableHeadCell>
                                <TableHeadCell></TableHeadCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                        {attachedGroupProgressTypes?.length > 0 &&
                            attachedGroupProgressTypes?.map((attachedGroupProgressType, index) => {
                                return (
                                    <TableRow key={index}>
                                        <TableDataCell><Button icon="fa-regular fa-pen" onClick={() => gotoEdit(attachedGroupProgressType.id)}></Button></TableDataCell>
                                        <TableDataCell>{attachedGroupProgressType.name}</TableDataCell>
                                        <TableDataCell>
                                            <span style={{ backgroundColor: attachedGroupProgressType?.color, padding: "2px 10px", borderRadius: "20%" }}></span>
                                            &nbsp;
                                            <span>{attachedGroupProgressType?.color}</span>
                                        </TableDataCell>
                                        <TableDataCell>{attachedGroupProgressType.reportColumnTitle}</TableDataCell>
                                        <TableDataCell>
                                            <Button title={"Izbrisi"} icon="fa-regular fa-trash-can" onClick={() => onRemove(attachedGroupProgressType.id)}></Button>
                                        </TableDataCell>
                                        <TableDataCell>
                                            <Button title={"Dodaj vrednost"} icon="fa-regular fa-plus" onClick={() => onAddOptions(attachedGroupProgressType.id)}></Button>
                                        </TableDataCell>
                                    </TableRow>
                                )
                        })}
                        </TableBody>
                    </ListBody>
                </List>
            </Main>
        </DefaultLayout>
    );
};

export default AddProgressType;